/**
 * Learn section — tab filtering, sliding indicator, carousel, video modal.
 * Safe for multiple sections on one page; skips missing optional elements.
 */
(function () {
  'use strict';

  function LearnSection(root) {
    this.root = root;
    this.selectedTab = root.dataset.defaultTab || 'read';

    this.tabs = root.querySelectorAll('[data-learn-tab]');
    this.indicator = root.querySelector('[data-tab-indicator]');
    this.tabLine = root.querySelector('[data-tab-line]');
    this.track = root.querySelector('[data-learn-track]');
    this.cards = root.querySelectorAll('[data-learn-card]');
    this.prevBtn = root.querySelector('[data-learn-prev]');
    this.nextBtn = root.querySelector('[data-learn-next]');
    this.viewAllLinks = root.querySelectorAll('[data-learn-view-all]');
    this.modal = root.querySelector('[data-learn-modal]');
    this.activeAudio = null;

    this._onResize = this.positionIndicator.bind(this);
    this._onEscape = this._handleEscape.bind(this);
    this._onTrackScroll = this.updateArrows.bind(this);

    this.bind();

    if (this.tabs.length) {
      this.setTab(this.selectedTab, { instant: true });
    }

    this.updateArrows();

    if (this.indicator && this.tabLine && this.tabs.length) {
      var self = this;
      requestAnimationFrame(function () {
        self.positionIndicator();
      });
    }
  }

  LearnSection.prototype._handleEscape = function (event) {
    if (!event || event.key !== 'Escape') return;
    if (!this.modal || !this.modal.classList.contains('is-open')) return;
    this.closeVideoModal();
  };

  LearnSection.prototype.bind = function () {
    var self = this;

    if (this.tabs.length) {
      this.tabs.forEach(function (tab) {
        tab.addEventListener('click', function () {
          var tabId = tab.getAttribute('data-learn-tab');
          if (!tabId) return;
          self.selectedTab = tabId;
          self.setTab(tabId);
        });
      });
    }

    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', function () {
        self.scrollCarousel(-1);
      });
    }

    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', function () {
        self.scrollCarousel(1);
      });
    }

    if (this.track) {
      this.track.addEventListener('scroll', this._onTrackScroll);
    }

    if (this.tabs.length && this.indicator && this.tabLine) {
      window.addEventListener('resize', this._onResize, { passive: true });
    }

    if (this.cards.length) {
      this.cards.forEach(function (card) {
        var contentType = card.getAttribute('data-content-type');
        var videoUrl = card.getAttribute('data-video-url');
        var hostedTemplate = card.querySelector('[data-hosted-video-template]');
        var isWatch = contentType === 'watch' && (videoUrl || hostedTemplate);

        if (isWatch) {
          var trigger = card.querySelector('[data-media-trigger]');
          if (!trigger) return;

          trigger.addEventListener('click', function (event) {
            event.preventDefault();
            event.stopPropagation();
            var titleEl = card.querySelector('.learn-section__card-title');
            var title = titleEl ? titleEl.textContent : '';

            if (hostedTemplate && hostedTemplate.innerHTML.trim()) {
              self.openMediaModal({ type: 'hostedVideo', html: hostedTemplate.innerHTML, title: title });
              return;
            }
            if (videoUrl) {
              self.openMediaModal({ type: 'videoUrl', url: videoUrl, title: title });
            }
          });
        }
      });
    }

    this.initAudioPlayers();

    if (this.modal) {
      var closeBtn = this.modal.querySelector('[data-modal-close]');
      if (closeBtn) {
        closeBtn.addEventListener('click', function () {
          self.closeVideoModal();
        });
      }

      this.modal.addEventListener('click', function (event) {
        if (event.target === self.modal) {
          self.closeVideoModal();
        }
      });

      document.addEventListener('keydown', this._onEscape);
    }
  };

  LearnSection.prototype.destroy = function () {
    window.removeEventListener('resize', this._onResize);

    if (this.track) {
      this.track.removeEventListener('scroll', this._onTrackScroll);
    }

    document.removeEventListener('keydown', this._onEscape);
    this.pauseAllAudio();
    this.closeVideoModal();
  };

  LearnSection.prototype.setTab = function (tabId, options) {
    options = options || {};
    var self = this;
    var active = tabId || 'read';

    if (this.tabs.length) {
      this.tabs.forEach(function (tab) {
        var isActive = tab.getAttribute('data-learn-tab') === active;
        tab.classList.toggle('is-active', isActive);
        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });
    }

    this.positionIndicator();
    this.pauseAllAudio();

    if (this.track && !options.instant) {
      this.track.classList.add('is-switching');
    }

    if (this.cards.length) {
      this.cards.forEach(function (card) {
        var type = card.getAttribute('data-content-type');
        var visible = type === active;
        card.classList.toggle('is-filtered-out', !visible);
      });
    }

    if (this.viewAllLinks.length) {
      this.viewAllLinks.forEach(function (link) {
        var linkTab = link.getAttribute('data-learn-view-all');
        if (linkTab === active) {
          link.removeAttribute('hidden');
        } else {
          link.setAttribute('hidden', '');
        }
      });
    }

    if (this.track) {
      window.setTimeout(function () {
        if (!self.track) return;
        self.track.classList.remove('is-switching');
        self.track.scrollLeft = 0;
        self.updateArrows();
      }, options.instant ? 0 : 200);
    }
  };

  LearnSection.prototype.positionIndicator = function () {
    if (!this.indicator || !this.tabLine || !this.tabs.length) return;

    var activeTab = this.root.querySelector('[data-learn-tab].is-active');
    if (!activeTab) return;

    var lineRect = this.tabLine.getBoundingClientRect();
    var tabRect = activeTab.getBoundingClientRect();

    if (!lineRect.width && !tabRect.width) return;

    this.indicator.style.width = tabRect.width + 'px';
    this.indicator.style.left = tabRect.left - lineRect.left + 'px';
  };

  LearnSection.prototype.scrollCarousel = function (direction) {
    if (!this.track) return;

    var visibleCard = this.track.querySelector('[data-learn-card]:not(.is-filtered-out)');
    if (!visibleCard) return;

    var gap = 20;
    try {
      gap = parseInt(window.getComputedStyle(this.track).gap, 10) || 20;
    } catch (e) {
      gap = 20;
    }

    var amount = (visibleCard.offsetWidth || 0) + gap;
    if (!amount) return;

    if (typeof this.track.scrollBy === 'function') {
      this.track.scrollBy({ left: direction * amount, behavior: 'smooth' });
    } else {
      this.track.scrollLeft += direction * amount;
    }
  };

  LearnSection.prototype.updateArrows = function () {
    if (!this.track) return;

    var maxScroll = this.track.scrollWidth - this.track.clientWidth - 2;

    if (this.prevBtn) {
      this.prevBtn.disabled = this.track.scrollLeft <= 2;
    }
    if (this.nextBtn) {
      this.nextBtn.disabled = this.track.scrollLeft >= maxScroll;
    }
  };

  LearnSection.prototype.openVideoModal = function (url, title) {
    this.openMediaModal({ type: 'videoUrl', url: url, title: title });
  };

  LearnSection.prototype.openMediaModal = function (payload) {
    if (!this.modal || !payload) return;

    var mediaWrap = this.modal.querySelector('[data-modal-media]');
    if (!mediaWrap) return;

    var inner = this.modal.querySelector('.learn-section__modal-inner');

    mediaWrap.innerHTML = '';
    if (inner) inner.classList.remove('is-audio');

    if (payload.type === 'hostedVideo' && payload.html) {
      mediaWrap.innerHTML = payload.html;
      this._playMediaElement(mediaWrap.querySelector('video'));
    } else if (payload.type === 'audio' && payload.url) {
      if (inner) inner.classList.add('is-audio');
      var audio = document.createElement('audio');
      audio.src = payload.url;
      audio.controls = true;
      audio.setAttribute('preload', 'metadata');
      mediaWrap.appendChild(audio);
      this._playMediaElement(audio);
    } else if (payload.type === 'videoUrl' && payload.url) {
      var embed = this.getEmbedUrl(payload.url);
      if (!embed || !embed.src) return;

      if (embed.type === 'iframe') {
        var iframe = document.createElement('iframe');
        iframe.src = embed.src;
        iframe.title = payload.title || '';
        iframe.setAttribute('allow', 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture');
        iframe.setAttribute('allowfullscreen', '');
        mediaWrap.appendChild(iframe);
      } else if (embed.type === 'video') {
        var video = document.createElement('video');
        video.src = embed.src;
        video.controls = true;
        video.setAttribute('playsinline', '');
        video.setAttribute('preload', 'metadata');
        mediaWrap.appendChild(video);
        this._playMediaElement(video);
      }
    }

    this.modal.classList.add('is-open');
    this.modal.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
  };

  LearnSection.prototype.closeVideoModal = function () {
    if (!this.modal) return;

    var mediaWrap = this.modal.querySelector('[data-modal-media]');
    if (mediaWrap) {
      // Explicitly stop media before clearing DOM (prevents background playback).
      var videos = mediaWrap.querySelectorAll('video');
      for (var v = 0; v < videos.length; v++) {
        try {
          videos[v].pause();
          videos[v].removeAttribute('src');
          if (typeof videos[v].load === 'function') videos[v].load();
        } catch (e) {}
      }

      var audios = mediaWrap.querySelectorAll('audio');
      for (var a = 0; a < audios.length; a++) {
        try {
          audios[a].pause();
          audios[a].removeAttribute('src');
          if (typeof audios[a].load === 'function') audios[a].load();
        } catch (e) {}
      }

      var iframes = mediaWrap.querySelectorAll('iframe');
      for (var i = 0; i < iframes.length; i++) {
        try {
          iframes[i].src = 'about:blank';
        } catch (e) {}
      }

      mediaWrap.innerHTML = '';
    }

    this.modal.classList.remove('is-open');
    this.modal.setAttribute('aria-hidden', 'true');
    var inner = this.modal.querySelector('.learn-section__modal-inner');
    if (inner) inner.classList.remove('is-audio');

    var anyOpen = document.querySelector('.learn-section__modal.is-open');
    if (!anyOpen) {
      document.body.style.overflow = '';
    }
  };

  LearnSection.prototype.formatAudioDuration = function (seconds) {
    if (!seconds || isNaN(seconds) || !isFinite(seconds)) return '--';

    var total = Math.max(0, Math.floor(seconds));
    var mins = Math.floor(total / 60);
    var secs = total % 60;

    if (mins >= 1) {
      return mins + ' min';
    }

    return '0:' + (secs < 10 ? '0' : '') + secs;
  };

  LearnSection.prototype.pauseAllAudio = function () {
    var players = this.root.querySelectorAll('[data-audio-player]');
    for (var i = 0; i < players.length; i++) {
      this.setAudioPlayerState(players[i], false);
    }
    this.activeAudio = null;
  };

  LearnSection.prototype.setAudioPlayerState = function (player, isPlaying) {
    if (!player) return;

    var audio = player.querySelector('[data-audio-element]');
    var playIcon = player.querySelector('.learn-section__audio-icon--play');
    var pauseIcon = player.querySelector('.learn-section__audio-icon--pause');
    var playBtn = player.querySelector('[data-audio-play]');

    if (audio && isPlaying === false) {
      try {
        audio.pause();
      } catch (e) {}
    }

    player.classList.toggle('is-playing', !!isPlaying);

    if (playIcon) playIcon.hidden = !!isPlaying;
    if (pauseIcon) pauseIcon.hidden = !isPlaying;
    if (playBtn) playBtn.setAttribute('aria-label', isPlaying ? 'Pause audio' : 'Play audio');
  };

  LearnSection.prototype.initAudioPlayers = function () {
    var self = this;
    var players = this.root.querySelectorAll('[data-audio-player]');

    players.forEach(function (player) {
      if (player.getAttribute('data-audio-init') === 'true') return;
      player.setAttribute('data-audio-init', 'true');

      var audio = player.querySelector('[data-audio-element]');
      var playBtn = player.querySelector('[data-audio-play]');
      var seek = player.querySelector('[data-audio-seek]');
      var durationEl = player.querySelector('[data-audio-duration]');

      if (!audio || !playBtn || !seek || !durationEl) return;

      var updateDuration = function () {
        durationEl.textContent = self.formatAudioDuration(audio.duration);
      };

      var syncSeekProgress = function () {
        seek.style.setProperty('--seek-percent', seek.value + '%');
      };

      var updateSeek = function () {
        if (!audio.duration || !isFinite(audio.duration)) return;
        seek.value = String((audio.currentTime / audio.duration) * 100);
        syncSeekProgress();
      };

      audio.addEventListener('loadedmetadata', updateDuration);
      audio.addEventListener('durationchange', updateDuration);

      if (audio.readyState >= 1) {
        updateDuration();
      }

      audio.addEventListener('timeupdate', updateSeek);

      // Reset the play button if the audio file cannot be loaded.
      audio.addEventListener('error', function () {
        self.setAudioPlayerState(player, false);
        if (self.activeAudio === audio) {
          self.activeAudio = null;
        }
      });

      syncSeekProgress();

      audio.addEventListener('ended', function () {
        seek.value = '0';
        syncSeekProgress();
        self.setAudioPlayerState(player, false);
        if (self.activeAudio === audio) {
          self.activeAudio = null;
        }
      });

      playBtn.addEventListener('click', function (event) {
        event.preventDefault();
        event.stopPropagation();

        if (audio.paused) {
          if (self.activeAudio && self.activeAudio !== audio) {
            var activePlayer = self.activeAudio.closest('[data-audio-player]');
            self.setAudioPlayerState(activePlayer, false);
          }

          self.activeAudio = audio;
          self.setAudioPlayerState(player, true);
          self._playMediaElement(audio);
          return;
        }

        self.setAudioPlayerState(player, false);
        if (self.activeAudio === audio) {
          self.activeAudio = null;
        }
      });

      seek.addEventListener('input', function () {
        syncSeekProgress();
        if (!audio.duration || !isFinite(audio.duration)) return;
        audio.currentTime = (parseFloat(seek.value, 10) / 100) * audio.duration;
      });

      seek.addEventListener('click', function (event) {
        event.stopPropagation();
      });
    });
  };

  LearnSection.prototype._playMediaElement = function (mediaEl) {
    if (!mediaEl || typeof mediaEl.play !== 'function') return;

    var playPromise = mediaEl.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(function () {});
    }
  };

  LearnSection.prototype.getEmbedUrl = function (url) {
    if (!url) return null;

    var ytMatch = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([\w-]+)/);
    if (ytMatch) {
      return {
        type: 'iframe',
        src: 'https://www.youtube.com/embed/' + ytMatch[1] + '?autoplay=1'
      };
    }

    var vimeoMatch = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
    if (vimeoMatch) {
      return {
        type: 'iframe',
        src: 'https://player.vimeo.com/video/' + vimeoMatch[1] + '?autoplay=1'
      };
    }

    return { type: 'video', src: url };
  };

  function init(root) {
    if (!root || root.nodeType !== 1) return null;
    if (root.getAttribute('data-learn-init') === 'true') return root._learnSection || null;

    root.setAttribute('data-learn-init', 'true');
    var instance = new LearnSection(root);
    root._learnSection = instance;
    return instance;
  }

  function destroy(root) {
    if (!root || root.nodeType !== 1) return;

    var instance = root._learnSection;
    if (instance && typeof instance.destroy === 'function') {
      instance.destroy();
    }

    delete root._learnSection;
    root.removeAttribute('data-learn-init');
  }

  function initAll(scope) {
    var container = scope && scope.nodeType === 1 ? scope : document;
    if (!container.querySelectorAll) return;

    var roots = container.querySelectorAll('[data-learn-section]');
    for (var i = 0; i < roots.length; i++) {
      init(roots[i]);
    }
  }

  function destroyAll(scope) {
    var container = scope && scope.nodeType === 1 ? scope : document;
    if (!container.querySelectorAll) return;

    var roots = container.querySelectorAll('[data-learn-section]');
    for (var i = 0; i < roots.length; i++) {
      destroy(roots[i]);
    }
  }

  document.addEventListener('DOMContentLoaded', function () {
    initAll(document);
  });

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    destroyAll(event.target);
  });

  document.addEventListener('shopify:section:reorder', function () {
    initAll(document);
  });
})();
